export type Locale = "en" | "fr";

export type LocalizedText = {
  readonly en: string;
  readonly fr: string;
};

export type SiteCta = {
  readonly label: LocalizedText;
  readonly shortLabel: LocalizedText;
  readonly destination: string;
  readonly external: boolean;
};

export type ServiceRole = "entry" | "implementation";

export type SiteServiceSlug =
  | "ai-verification-review"
  | "production-ai-systems"
  | "agentic-ai-workflow-automation"
  | "data-platforms"
  | "decision-intelligence";

export type ImplementationServiceSlug = Exclude<SiteServiceSlug, "ai-verification-review">;

export type SiteService = {
  readonly slug: SiteServiceSlug;
  readonly role: ServiceRole;
  readonly href: string;
  readonly name: LocalizedText;
  readonly description: LocalizedText;
};

export type SiteIndustry = {
  readonly slug: string;
  readonly href: string;
  readonly label: LocalizedText;
};

export const SITE_URL = "https://vert-ai.com";
export const SITE_NAME = "Vert AI";
export const SITE_LOGO_IMAGE = `${SITE_URL}/vert-logo.png`;
export const SITE_OG_IMAGE = `${SITE_URL}/og-image.png`;
export const BOOKING_URL = "https://calendly.com/d/cvpp-rwv-v5j";
export const CONTACT_EMAIL = "guillaume.belisle@vert-ai.com";
export const FOUNDER_NAME = "Guillaume Belisle";
export const LINKEDIN_URL = "https://www.linkedin.com/in/guillaume-belisle";

export const siteContent = {
  brand: {
    companyName: SITE_NAME,
    shortPositioning: {
      en: "Fix costly commerce workflows with production AI, reliable data, and measurable operating control.",
      fr: "Corriger les processus coûteux du commerce grâce à une IA en production, des données fiables et un contrôle opérationnel mesurable.",
    },
    longPositioning: {
      en: "Vert AI fixes costly workflows and recurring decisions for mid-market commerce operators by redesigning the work and building the AI, data, controls, and measurement needed to run it reliably.",
      fr: "Vert AI corrige les processus coûteux et les décisions récurrentes des entreprises de commerce de taille intermédiaire en repensant le travail et en construisant l’IA, les données, les contrôles et la mesure nécessaires à une exploitation fiable.",
    },
    audience: {
      en: "founders, COOs, operations leaders, and data leaders in operations-led mid-market commerce companies.",
      fr: "fondateurs, chefs de l’exploitation, responsables des opérations et responsables des données au sein d’entreprises de commerce de taille intermédiaire.",
    },
    geography: {
      en: "Based in Canada, serving Canadian companies first and US companies selectively.",
      fr: "Établie au Canada, Vert AI sert d’abord les entreprises canadiennes et accompagne certaines entreprises américaines.",
    },
    geographyUsageGuidance: {
      en: "Do not over-emphasize location unless useful for SEO/contact.",
      fr: "Ne pas surexposer la localisation sauf lorsque c’est utile pour le SEO ou le contact.",
    },
  },
  ctas: {
    verificationReview: {
      label: {
        en: "Apply for the AI Verification Review",
        fr: "Présenter une demande d’examen de vérification de l’IA",
      },
      shortLabel: {
        en: "Apply for AI Review",
        fr: "Demander un examen de l’IA",
      },
      destination: "/apply/ai-verification-review",
      external: false,
    },
    reviewOffer: {
      label: {
        en: "Review a Live AI Initiative",
        fr: "Faire examiner une initiative d’IA en exploitation",
      },
      shortLabel: {
        en: "Review a Live AI Initiative",
        fr: "Faire examiner une initiative d’IA",
      },
      destination: "/services/ai-verification-review",
      external: false,
    },
    productionSystems: {
      label: {
        en: "Explore Production AI Systems",
        fr: "Explorer les systèmes d’IA en production",
      },
      shortLabel: {
        en: "Production AI Systems",
        fr: "Systèmes d’IA en production",
      },
      destination: "/services/production-ai-systems",
      external: false,
    },
    primary: {
      label: {
        en: "Discuss a costly workflow",
        fr: "Discuter d’un processus coûteux",
      },
      shortLabel: {
        en: "Discuss a costly workflow",
        fr: "Discuter d’un processus coûteux",
      },
      destination: "/contact",
      external: false,
    },
    secondary: {
      label: {
        en: "Assess your AI readiness",
        fr: "Évaluer votre préparation à l’IA",
      },
      shortLabel: {
        en: "Assess your AI readiness",
        fr: "Évaluer votre préparation à l’IA",
      },
      destination: "/ai-readiness-scorecard",
      external: false,
    },
    proof: {
      label: {
        en: "Review real engagements",
        fr: "Voir des mandats réels",
      },
      shortLabel: {
        en: "Review real engagements",
        fr: "Voir des mandats réels",
      },
      destination: "/work",
      external: false,
    },
  },
  messagePillars: [
    {
      en: "Choose the right workflow before building.",
      fr: "Choisir le bon processus opérationnel avant de construire.",
    },
    {
      en: "Define risk, ownership, human review, and escalation before automating.",
      fr: "Définir le risque, la responsabilité, la validation humaine et le transfert à un niveau supérieur avant d’automatiser.",
    },
    {
      en: "Fix the data foundation required for useful AI.",
      fr: "Mettre en place les fondations de données nécessaires à un système fiable.",
    },
    {
      en: "Build an operating system, not an isolated demo.",
      fr: "Construire un système exploitable, pas une démonstration isolée.",
    },
    {
      en: "Measure business value and adoption.",
      fr: "Mesurer la valeur opérationnelle et l’adoption.",
    },
    {
      en: "Operate with evaluation, monitoring, human review, and governance.",
      fr: "Exploiter avec évaluation, surveillance, validation humaine et gouvernance.",
    },
  ],
  serviceCatalogue: [
    {
      slug: "ai-verification-review",
      role: "entry",
      href: "/services/ai-verification-review",
      name: {
        en: "AI Verification & Production Readiness Review",
        fr: "Examen de vérification de l’IA et de préparation à la production",
      },
      description: {
        en: "When leadership must fund, launch, expand, pause, or stop one AI initiative: test the decision against evidence, controls, ownership, and economics.",
        fr: "Lorsque la direction doit financer, lancer, élargir, mettre en pause ou arrêter une initiative d’IA : vérifier la décision selon les preuves, les contrôles, les responsabilités et la viabilité économique.",
      },
    },
    {
      slug: "production-ai-systems",
      role: "implementation",
      href: "/services/production-ai-systems",
      name: {
        en: "Production AI Systems",
        fr: "Systèmes d’IA en production",
      },
      description: {
        en: "When the workflow is known: redesign it, automate the right steps, integrate the system, and operate it with evidence and control.",
        fr: "Lorsque le processus est connu : le repenser, automatiser les bonnes étapes, intégrer le système et l’exploiter avec des preuves et des contrôles.",
      },
    },
    {
      slug: "agentic-ai-workflow-automation",
      role: "implementation",
      href: "/solutions/ai-agents-workflow-automation",
      name: {
        en: "Agentic AI & Workflow Automation",
        fr: "IA agentique et automatisation des processus",
      },
      description: {
        en: "When multi-step work needs controlled orchestration: connect tools, rules, human approvals, exception handling, and observable agent behaviour.",
        fr: "Lorsqu’un travail en plusieurs étapes exige une orchestration contrôlée : relier les outils, les règles, les approbations humaines, le traitement des exceptions et l’observation du comportement des agents.",
      },
    },
    {
      slug: "data-platforms",
      role: "implementation",
      href: "/services/data-platforms",
      name: {
        en: "Data Platforms for AI",
        fr: "Plateformes de données pour l’IA",
      },
      description: {
        en: "When unreliable data blocks action: build the smallest governed data path the valuable workflow actually needs.",
        fr: "Lorsque des données peu fiables bloquent l’action : construire le plus petit parcours de données gouverné dont le processus prioritaire a réellement besoin.",
      },
    },
    {
      slug: "decision-intelligence",
      role: "implementation",
      href: "/services/decision-intelligence",
      name: {
        en: "Decision Intelligence",
        fr: "Intelligence décisionnelle",
      },
      description: {
        en: "When recurring decisions stay slow or inconsistent: connect signals, policy, recommendations, action, and outcome feedback.",
        fr: "Lorsque les décisions récurrentes restent lentes ou incohérentes : relier les signaux, les règles, les recommandations, l’action et la rétroaction sur les résultats.",
      },
    },
  ] satisfies SiteService[],
  industries: [
    {
      slug: "retail-ecommerce",
      href: "/industries/retail-ecommerce-ai",
      label: {
        en: "Retail & Ecommerce",
        fr: "Commerce de détail et commerce électronique",
      },
    },
    {
      slug: "cpg",
      href: "/industries/cpg-ai",
      label: {
        en: "CPG",
        fr: "Biens de consommation",
      },
    },
    {
      slug: "franchise-multi-location",
      href: "/industries/franchise-multi-location-ai",
      label: {
        en: "Franchise & Multi-location Operators",
        fr: "Franchises et réseaux multisites",
      },
    },
  ] satisfies SiteIndustry[],
  proof: {
    founderLedDelivery: {
      en: "Founder-led scoping, architecture, and implementation with senior technical involvement close to production decisions.",
      fr: "Cadrage et décisions techniques clés dirigés par le fondateur, avec des spécialistes chevronnés mobilisés lorsque le mandat l’exige.",
    },
    productionExperience: {
      en: "Production AI, ML, data platform, decision-system, and automation experience applied from strategy through build.",
      fr: "Expérience en IA de production, apprentissage automatique, plateformes de données, systèmes décisionnels et automatisation, du cadrage à l’exploitation.",
    },
    selectedOperatorExperience: {
      en: "Founder experience includes designing and shipping production AI, ML, and data systems in operator environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware. These organizations are not presented as Vert AI clients.",
      fr: "L’expérience professionnelle du fondateur comprend la conception et la livraison de systèmes d’IA, d’apprentissage automatique et de données en production dans des environnements comme Louis Vuitton, Canada Goose, Lowe’s et Home Hardware. Ces entreprises ne sont pas présentées comme des clientes de Vert AI.",
    },
  },
  trustStandards: [
    {
      en: "Evaluation harnesses",
      fr: "Grilles et bancs d’évaluation",
    },
    {
      en: "Prompt and workflow standards",
      fr: "Normes pour les instructions et les processus",
    },
    {
      en: "Human-in-the-loop controls",
      fr: "Contrôles de validation humaine",
    },
    {
      en: "Escalation logic",
      fr: "Règles de transfert",
    },
    {
      en: "Observability and monitoring",
      fr: "Observabilité et surveillance",
    },
    {
      en: "Privacy and security review",
      fr: "Examen de la confidentialité et de la sécurité",
    },
    {
      en: "Data quality checks",
      fr: "Contrôles de qualité des données",
    },
    {
      en: "Ownership and governance",
      fr: "Responsabilité et gouvernance",
    },
    {
      en: "ROI and adoption measurement",
      fr: "Mesure du rendement et de l’adoption",
    },
  ],
  seo: {
    defaultTitle: "Fix Costly Commerce Workflows with Production AI | Vert AI",
    defaultDescription:
      "Vert AI fixes costly commerce workflows by redesigning the work and building the production AI, reliable data, controls, and measurement needed to operate it.",
    defaultImage: SITE_OG_IMAGE,
    defaultImageAlt: "Vert AI production AI systems",
    defaultImageAltFr: "Systèmes d’IA en production de Vert AI",
  },
} as const;

export const serviceCatalogue = siteContent.serviceCatalogue;
export const entryEngagement = serviceCatalogue.find((service) => service.role === "entry")!;
export const implementationServices = serviceCatalogue.filter((service) => service.role === "implementation");

export function localize(text: LocalizedText, locale: Locale = "en") {
  return text[locale] || text.en;
}

export function getCtaLabel(
  cta: SiteCta,
  locale: Locale = "en",
  variant: "label" | "shortLabel" = "label",
) {
  return localize(cta[variant], locale);
}
